import * as React from "react"

import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"
import { Lightbulb } from "lucide-react"

import { cn } from "../../cn"

/**
 * House timing for every tooltip. The app's single provider owns these and
 * nothing below it sets a delay of its own, because `skipDelayDuration` only
 * means anything across a group of tooltips sharing one provider.
 */
const TOOLTIP_DELAY_DURATION = 100
const TOOLTIP_SKIP_DELAY_DURATION = 100

/**
 * House placement for every tooltip: above the thing it labels. Radix already
 * resolves to the top side and leaves `avoidCollisions` on, so a tooltip that
 * would run off the viewport flips to the bottom on its own and needs no
 * positioning logic here. Naming the default anyway keeps the rule readable in
 * our own code rather than inherited from the library, so a call site that
 * wants something else has to say so.
 */
const TOOLTIP_SIDE = "top"
/** Gap between the tooltip and its trigger. */
const TOOLTIP_SIDE_OFFSET = 4
/**
 * Breathing room a flipped or shifted tooltip keeps from the viewport edge.
 * Radix leaves this at 0, which lets a tooltip sit flush against the edge it
 * just avoided.
 */
const TOOLTIP_COLLISION_PADDING = 8

/**
 * Whether a `TooltipProvider` is already above us. Radix throws without one,
 * which would make every tree holding an icon button — a test, a story, a
 * fragment rendered on its own — responsible for knowing that the button has a
 * tooltip inside it. `Tooltip` supplies its own when this is false, so the app's
 * single provider still owns timing wherever it is mounted and nowhere grows a
 * second delay group.
 */
const TooltipProviderPresence = React.createContext(false)

/**
 * A disabled control emits no pointer events at all, so a click on it never
 * reaches an ancestor's handler — which is why a disabled button inside a
 * clickable card does nothing rather than selecting the card. The stand-in
 * below is an ordinary element and would happily pass those clicks up, so it
 * re-swallows exactly what the control no longer emits. Only propagation stops:
 * `preventDefault` would tell Radix's composed handlers the event was already
 * handled and take the tooltip down with it.
 */
const swallowPointerEvent = (event: React.SyntheticEvent) => {
  event.stopPropagation()
}

const DISABLED_STAND_IN_HANDLERS = {
  onClick: swallowPointerEvent,
  onMouseDown: swallowPointerEvent,
  onMouseUp: swallowPointerEvent,
  onPointerDown: swallowPointerEvent,
  onPointerUp: swallowPointerEvent,
} as const

const TooltipProvider = ({
  delayDuration = TOOLTIP_DELAY_DURATION,
  skipDelayDuration = TOOLTIP_SKIP_DELAY_DURATION,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipProviderPresence.Provider value>
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    >
      {children}
    </TooltipPrimitive.Provider>
  </TooltipProviderPresence.Provider>
)

/** Fade, zoom, and slide in from the trigger's side; reversed on close. */
const TOOLTIP_MOTION = [
  "animate-in fade-in-0 zoom-in-95",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
]

const tooltipContentVariants = cva(
  [
    "z-50 overflow-hidden rounded-md text-sm text-coval-text",
    "border border-coval-border bg-surface-default",
    // A sentence of explanation wraps into a readable column instead of
    // stretching across the page, and an unbroken token (a URL, a template
    // variable) breaks rather than overflowing the panel. A caller with wider
    // content raises the cap through `className`.
    "max-w-xs wrap-break-word",
  ],
  {
    variants: {
      variant: {
        // Labels and tips run on the inverse of the page's theme: dark panel
        // with light text on a light page, and the reverse on a dark one. That
        // is what keeps a short tooltip from reading as one more panel among
        // the surfaces it floats over. `coval-theme-invert` flips the whole
        // token set for the subtree, so caller-supplied content — muted text,
        // status colours, nested chips — lands on the inverted surface with
        // its contrast intact.
        //
        // A label for the thing under the pointer: tight, and lifted just
        // enough to separate it from what it labels.
        default: ["coval-theme-invert px-2 py-1 shadow-md", ...TOOLTIP_MOTION],
        // Guidance the user is meant to read and act on. Roomier and more
        // strongly lifted, so several lines of advice read as a card instead
        // of a smudge, and led by a lightbulb so its purpose is clear before
        // the words are.
        tip: ["coval-theme-invert px-4 py-3.5 shadow-lg", ...TOOLTIP_MOTION],
        // The full text behind a truncated cell — a test case, a transcript,
        // a JSON payload. Long-form reading stays on the page's own theme, the
        // way a popover does, because a dense block of inverted text is hard
        // to read. The body is the one scroll container, so callers pass bare
        // content and never add a max height or overflow of their own. It
        // appears without motion: a block of text that zooms and slides in is
        // harder to start reading than one that is simply there. Its width
        // also yields to a narrow viewport, keeping clear of both edges.
        panel: "max-w-[min(28rem,calc(100vw-2rem))] shadow-lg",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

export interface TooltipProps extends VariantProps<
  typeof tooltipContentVariants
> {
  content: React.ReactNode
  children: React.ReactElement
  /**
   * Which side of the trigger to prefer. Defaults to `"top"`, which is the
   * house rule; Radix flips to the opposite side by itself when the preferred
   * one would leave the viewport, so this is for deliberate exceptions —
   * `"left"` / `"right"` in a narrow rail, or `"bottom"` under a sticky header
   * that Radix cannot see — not for collision handling.
   */
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  /**
   * Holds the tooltip open on top of the usual hover and focus behavior, for
   * callers that also use it as action feedback (e.g. a copy confirmation that
   * has to outlive the click, which dismisses the hover tooltip).
   */
  forceOpen?: boolean
  /** Control interaction-driven visibility, for example to dismiss on scroll. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * Whether pointer and focus interactions can open the tooltip. Disable this
   * for feedback-only tooltips attached to a large trigger surface.
   */
  openOnInteraction?: boolean
  /**
   * Extra classes for the tooltip content. Merged after the defaults, so it can
   * override them (e.g. raising the z-index when the tooltip is opened from
   * inside a high-stacking overlay that would otherwise occlude it).
   */
  className?: string
  /**
   * Whether the trigger is disabled. A disabled control emits no pointer events
   * and takes no focus, so a tooltip hanging off it never opens — exactly where
   * the user most needs to know why the control is unavailable. Set this and
   * the tooltip stands in for the control: a focusable wrapper announced as a
   * disabled button under the control's own name, with the control itself taken
   * out of the tab order and hidden from assistive technology so the pair is
   * announced once.
   */
  triggerDisabled?: boolean
  /** Data attributes forwarded to the portaled tooltip content. */
  contentDataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

/**
 * The words a control shows, as far as its own markup can say — nested
 * elements included, so a label wrapped in a `<span>` reads the same as a bare
 * one. Anything a child component renders is beyond reach here, and comes back
 * as nothing rather than as a guess.
 */
export function renderedText(node: React.ReactNode): string | undefined {
  const words: string[] = []

  const collect = (value: React.ReactNode): void => {
    if (typeof value === "string" || typeof value === "number") {
      words.push(String(value))
      return
    }
    if (Array.isArray(value)) {
      value.forEach(collect)
      return
    }
    if (React.isValidElement<{ children?: React.ReactNode }>(value)) {
      collect(value.props.children)
    }
  }

  collect(node)

  const text = words.join(" ").replace(/\s+/g, " ").trim()
  return text || undefined
}

/**
 * State and relationships the stand-in repeats off the control it speaks for.
 * The control goes behind `aria-hidden`, so whatever it said about itself is
 * only said once the stand-in says it too: a disabled toggle that keeps its
 * name but loses `aria-pressed` reads as a plain button. Two are left out on
 * purpose: `aria-describedby`, because the tooltip is the trigger's
 * description and Radix owns that attribute while it is open, and
 * `aria-checked`, which the stand-in's own `button` role cannot carry.
 */
interface CarriedStandInState {
  "aria-busy"?: React.AriaAttributes["aria-busy"]
  "aria-pressed"?: React.AriaAttributes["aria-pressed"]
  "aria-expanded"?: React.AriaAttributes["aria-expanded"]
  "aria-haspopup"?: React.AriaAttributes["aria-haspopup"]
  "aria-current"?: React.AriaAttributes["aria-current"]
  "aria-controls"?: React.AriaAttributes["aria-controls"]
}

/** Hands a node to a ref of either shape, leaving a missing one alone. */
function assignRef<T>(ref: React.Ref<T> | undefined, node: T | null): void {
  if (typeof ref === "function") {
    ref(node)
  } else if (ref) {
    ref.current = node
  }
}

/** What the stand-in reads off the control it speaks for. */
interface StandInTriggerProps extends CarriedStandInState {
  className?: string
  children?: React.ReactNode
  "aria-label"?: string
  "aria-labelledby"?: string
  "aria-hidden"?: React.AriaAttributes["aria-hidden"]
  tabIndex?: number
}

const Tooltip = ({
  content,
  children,
  side = TOOLTIP_SIDE,
  align,
  variant,
  forceOpen = false,
  open,
  onOpenChange,
  openOnInteraction = true,
  className,
  triggerDisabled = false,
  contentDataAttributes,
}: TooltipProps) => {
  const [hoverOpen, setHoverOpen] = React.useState(false)
  const hasProvider = React.useContext(TooltipProviderPresence)
  const standInRef = React.useRef<HTMLSpanElement>(null)
  const controlRef = React.useRef<HTMLElement | null>(null)
  const wasDisabled = React.useRef(triggerDisabled)
  const detachedTriggerHadFocus = React.useRef(false)

  /**
   * The control's own ref, so composing ours onto it does not cost the caller
   * theirs. Radix's `asChild` trigger already hands the child a ref, so a
   * valid child accepts one either way.
   */
  const childRef = React.isValidElement<{ ref?: React.Ref<HTMLElement> }>(
    children,
  )
    ? children.props.ref
    : undefined

  const setControlRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (!node && controlRef.current === document.activeElement) {
        detachedTriggerHadFocus.current = true
      }
      controlRef.current = node
      assignRef(childRef, node)
    },
    [childRef],
  )

  const setStandInRef = React.useCallback((node: HTMLSpanElement | null) => {
    if (!node && standInRef.current === document.activeElement) {
      detachedTriggerHadFocus.current = true
    }
    standInRef.current = node
  }, [])

  /** Ref detachment captures focus before removing the trigger drops it to the document; body focus alone does not mean this control held it. */
  React.useLayoutEffect(() => {
    const swapped = triggerDisabled !== wasDisabled.current
    const hadFocus = detachedTriggerHadFocus.current
    wasDisabled.current = triggerDisabled
    detachedTriggerHadFocus.current = false

    if (!swapped || !hadFocus || document.activeElement !== document.body) {
      return
    }

    if (triggerDisabled) {
      standInRef.current?.focus()
    } else {
      controlRef.current?.focus()
    }
  })

  const trigger = React.useMemo(() => {
    if (
      !triggerDisabled ||
      !React.isValidElement<StandInTriggerProps>(children)
    ) {
      return React.isValidElement<StandInTriggerProps>(children)
        ? React.cloneElement(children, { ref: setControlRef } as never)
        : children
    }

    const labelledBy = children.props["aria-labelledby"]
    /**
     * The stand-in has to carry the control's name, because the control goes
     * behind `aria-hidden` so the pair is announced once. The control's own
     * name comes first — its label, else the words it shows — and the
     * tooltip's words only name a control that has no name of its own.
     */
    const label =
      children.props["aria-label"] ??
      renderedText(children.props.children) ??
      (typeof content === "string" ? content : undefined)

    /**
     * A control named by markup we cannot read would leave the stand-in an
     * unnamed button, which is worse than a tooltip that does not open. Leave
     * such a control alone and let the caller give it a label.
     */
    if (!labelledBy && !label) {
      return children
    }

    return (
      // Stays hoverable and focusable while the control inside it is neither,
      // so a disabled control can still explain itself. `inline-flex` keeps the
      // stand-in out of the way of whatever lays the control out.
      <span
        ref={setStandInRef}
        className="inline-flex rounded-md"
        tabIndex={0}
        role="button"
        aria-disabled
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        aria-busy={children.props["aria-busy"]}
        aria-pressed={children.props["aria-pressed"]}
        aria-expanded={children.props["aria-expanded"]}
        aria-haspopup={children.props["aria-haspopup"]}
        aria-current={children.props["aria-current"]}
        aria-controls={children.props["aria-controls"]}
        {...DISABLED_STAND_IN_HANDLERS}
      >
        {React.cloneElement(children, {
          // Letting the pointer through hands the event to the stand-in, which
          // is not disabled.
          className: cn(children.props.className, "pointer-events-none"),
          // `disabled` means nothing to a slotted anchor, so the stand-in's tab
          // stop would be a second one rather than the only one — and a
          // focusable element under `aria-hidden` is a trap on its own. Taking
          // the control out of the tab order leaves exactly the stand-in to
          // focus. A disabled `<button>` is unfocusable already and ignores it.
          tabIndex: -1,
          "aria-hidden": true,
        } as StandInTriggerProps)}
      </span>
    )
  }, [children, content, setControlRef, setStandInRef, triggerDisabled])

  const tooltip = (
    <TooltipPrimitive.Root
      open={forceOpen || (openOnInteraction && (open ?? hoverOpen))}
      onOpenChange={
        openOnInteraction
          ? (nextOpen) => {
              if (open === undefined) {
                setHoverOpen(nextOpen)
              }
              onOpenChange?.(nextOpen)
            }
          : undefined
      }
    >
      <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          {...contentDataAttributes}
          side={side}
          align={align}
          sideOffset={TOOLTIP_SIDE_OFFSET}
          collisionPadding={TOOLTIP_COLLISION_PADDING}
          className={cn(tooltipContentVariants({ variant }), className)}
        >
          {variant === "tip" ? (
            <div className="flex items-start gap-3">
              <Lightbulb
                className="mt-0.5 size-4 shrink-0 text-coval-warning-text"
                aria-hidden="true"
              />
              <div className="min-w-0 leading-5">{content}</div>
            </div>
          ) : variant === "panel" ? (
            <div className="max-h-[min(18rem,var(--radix-tooltip-content-available-height))] overflow-y-auto overscroll-contain px-3 py-2.5 leading-5 whitespace-pre-wrap wrap-break-word">
              {content}
            </div>
          ) : (
            content
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )

  return hasProvider ? tooltip : <TooltipProvider>{tooltip}</TooltipProvider>
}

Tooltip.displayName = "Tooltip"

export { Tooltip, TooltipProvider, tooltipContentVariants }
